import axios from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook } from '../utils';

export class BybitStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const res = await axios.get<{
        result: { list: { priceFilter: { tickSize: string } }[] };
      }>(`https://api.bybit.com/v5/market/instruments-info?category=spot&symbol=${this.pair}`);

      let prec = 1;

      if (res.data) {
        prec = res.data.result.list[0].priceFilter.tickSize.split('.')[1].length;
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Bybit ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    from,
    to,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.bybit];

    try {
      const result = await axios.get<{ result: { list: string[][] } }>(
        'https://api.bybit.com/v5/market/kline',
        {
          params: {
            category: 'spot',
            symbol: `${base.toUpperCase()}${quote.toUpperCase()}`,
            limit,
            interval: innerInterval,
            from: from,
            to: to,
          },
        },
      );

      return (result.data.result.list ?? []).map(el => [
        Number(el[0]),
        el[1],
        el[2],
        el[3],
        el[4],
        el[5],
      ]) as KLine[];
    } catch (error) {
      console.log('Bybit klines error: ', error);
      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const result = await axios.get<{
        result: {
          list: {
            side: string;
            price: string;
            size: string;
            time: string;
          }[];
        };
      }>('https://api.bybit.com/v5/market/recent-trade', {
        params: {
          category: 'spot',
          symbol: `${base.toUpperCase()}${quote.toUpperCase()}`,
          limit,
        },
      });

      return (
        (result.data.result.list ?? []).map(item => ({
          price: item.price,
          quantity: item.size,
          timestamp: item.time,
          side: item.side === 'Sell' ? 'sell' : 'buy',
        })) ?? []
      );
    } catch (error) {
      console.log('Bybit trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket('wss://stream.bybit.com/v5/public/spot', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    const depthWs = new ReconnectingWebSocket('wss://ws2.bybit.com/spot/ws/quote/v2', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;
    this.depthWs = depthWs;

    const intervalArg = {
      60000: 1,
      300000: 5,
      900000: 15,
      3600000: 60,
      14400000: 240,
      86400000: 'D',
      604800000: 'W',
    }[interval];

    const tickerArg = `tickers.${this.pair}`;
    const klineArg = `kline.${intervalArg}.${this.pair}`;
    const tradeArg = `publicTrade.${this.pair}`;

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send(JSON.stringify({ op: 'ping' }));
      }

      if (this.depthWs) {
        this.depthWs.send(JSON.stringify({ op: 'ping' }));
      }
    }, 30 * 1000);

    this.ws.onopen = () => {
      ws.send(
        JSON.stringify({
          op: 'subscribe',
          args: [tickerArg],
        }),
      );

      ws.send(
        JSON.stringify({
          op: 'subscribe',
          args: [klineArg],
        }),
      );

      ws.send(
        JSON.stringify({
          op: 'subscribe',
          args: [tradeArg],
        }),
      );
    };

    this.ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.topic === tickerArg && data.data) {
          const { volume24h, price24hPcnt, lastPrice, highPrice24h, lowPrice24h, turnover24h } =
            data.data;

          this.onTickerUpdate({
            baseVolume: volume24h,
            change: normalizeNumber(Number(price24hPcnt) * 100),
            close: lastPrice,
            high: highPrice24h,
            last: lastPrice,
            low: lowPrice24h,
            quoteVolume: turnover24h,
          });
        }

        if (data.topic === klineArg && data.data) {
          const { start, open, high, low, close, volume } = data.data[0];

          this.klineUpdate([Math.floor(start / 1000), open, high, low, close, volume]);
        }

        if (data.topic === tradeArg && data.data[0]) {
          const { p, v, T, S } = data.data[0];

          this.onTradeUpdate({
            price: p,
            quantity: v,
            timestamp: T,
            side: S.toLowerCase(),
          });
        }
      } catch (error) {
        console.log('Bybit socket error', error);
      }
    };

    this.depthWs.onopen = () => {
      depthWs.send(
        JSON.stringify({
          event: 'sub',
          limit: this.depth,
          params: { binary: false, dumpScale: this.precision },
          symbol: this.pair,
          topic: 'mergedDepth',
        }),
      );
    };

    this.depthWs.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.topic === 'mergedDepth' && data.data[0]) {
          const { a, b } = data.data[0];

          this.asks = a;
          this.bids = b;

          const orderbook = {
            bids: (b as [string, string][]).map(el => [
              Number(el[0]).toFixed(this.precision),
              el[1],
            ]),
            asks: (a as [string, string][]).map(el => [
              Number(el[0]).toFixed(this.precision),
              el[1],
            ]),
          };

          this.onOrderbookUpdate(
            aggregateOrderbook(orderbook.asks, orderbook.bids, this.selectedPrecision),
          );
        }
      } catch (error) {
        console.log('Bybit depth socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }

    if (this.depthWs) {
      this.depthWs.close();
    }

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  setOrderbookDepth(depth: number) {
    this.depth = depth;

    if (this.depthWs) {
      this.depthWs.send(
        JSON.stringify({
          event: 'cancel',
          limit: this.depth,
          params: { binary: false, dumpScale: this.selectedPrecision },
          symbol: this.pair,
          topic: 'mergedDepth',
        }),
      );

      this.depthWs.send(
        JSON.stringify({
          event: 'sub',
          limit: this.depth,
          params: { binary: false, dumpScale: this.selectedPrecision },
          symbol: this.pair,
          topic: 'mergedDepth',
        }),
      );
    }
  }

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    if (this.depthWs) {
      this.depthWs.send(
        JSON.stringify({
          event: 'cancel',
          limit: this.depth,
          params: { binary: false, dumpScale: this.selectedPrecision },
          symbol: this.pair,
          topic: 'mergedDepth',
        }),
      );

      this.selectedPrecision = tick;

      this.depthWs.send(
        JSON.stringify({
          event: 'sub',
          limit: this.depth,
          params: { binary: false, dumpScale: this.selectedPrecision },
          symbol: this.pair,
          topic: 'mergedDepth',
        }),
      );
    }
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new BybitStrategy(args);

    await instance.fetch();

    return instance;
  };
}
